// Client-side Slow Burn helpers on top of @wbwl/rules. As in the daily game, the browser checks a
// word at once and the server's answer wins. The browser only knows the flame columns up to the
// row after the one you're on, so the edition built here pads the rest; only the current row's
// column is ever read for a move.
import type { SlowBurnInfo, SlowBurnRun } from '@wbwl/contract'
import { scoreRun, SLOW_BURN, type RunRejection, type RunState, type SlowBurnEdition } from '@wbwl/rules'
import { SITE } from './game.ts'

export function toSlowBurn(info: SlowBurnInfo, run: SlowBurnRun): SlowBurnEdition {
  const cols = [...run.cols]
  while (cols.length < info.rows) cols.push(0)
  return { number: info.number, date: info.date, seed: '', rulesVersion: info.rulesVersion, wordsVersion: info.wordsVersion, cols, gold: info.gold }
}

/** The letters a run hasn't burned, A to Z. */
export const lettersStanding = (ashes: readonly string[]): string[] => [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'].filter((l) => !ashes.includes(l))

export const toRunState = (run: SlowBurnRun): RunState => ({ rows: run.rows, ashes: run.ashes, ended: run.ended })

/** A run built locally from a state, shown at once before the server answers (it brings the next column). */
export function localRun(info: SlowBurnInfo, prev: SlowBurnRun, state: RunState): SlowBurnRun {
  const score = scoreRun(info, state)
  return {
    ...prev,
    rows: state.rows,
    ashes: state.ashes,
    ended: state.ended,
    cols: state.ended ? prev.cols.slice(0, state.rows.length) : prev.cols,
    fuse: state.ended ? null : prev.fuse,
    scores: { rows: score.rows, points: score.points },
    depth: score.depth,
    startedAt: prev.startedAt ?? new Date().toISOString(),
  }
}

export function runRejectionText(r: RunRejection, word: string): string {
  switch (r.reason) {
    case 'wrong_length': return 'Five letters, please'
    case 'not_a_word': return `${word} isn't in the word list`
    case 'already_played': return `You played ${word} on row ${r.row + 1}`
    case 'burned_letter': return `${r.letters.join(', ')} already burned`
    case 'wrong_phase': return 'This run is over'
  }
}

/** The share line: a square per row reached (gold when the burned letter was gold), white for the rest. */
export function runShareText(info: SlowBurnInfo, run: SlowBurnRun): string {
  let fuse = ''
  for (let i = 0; i < SLOW_BURN.rows; i++) fuse += i < run.rows.length ? (info.gold[run.ashes[i]] ? '🟨' : '🟥') : '⬜'
  return `Slow Burn No. ${info.number}\nRow ${run.depth}/${info.rows} · ${run.scores.points.toLocaleString('en-US')} pts\n${fuse}\n\n${SITE}/slow-burn`
}
